import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, BadgeCheck, Upload, Printer, Loader2, Search, User } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  super_admin: "Direction Système", founder: "Fondateur", director: "Directeur Général",
  admin: "Administration", finance: "Finance", scolarite: "Resp. Scolarité",
  teacher: "Enseignant(e)", nurse: "Infirmerie", canteen: "Cantine",
};

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

async function openPdf(url) {
  const resp = await api.get(url, { responseType: "blob" });
  const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
  window.open(blobUrl, "_blank");
}

function PhotoCell({ path, initials, version }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    let alive = true;
    if (path) {
      api.get(path, { responseType: "blob" })
        .then(r => { if (alive) { url = URL.createObjectURL(r.data); setSrc(url); } })
        .catch(() => { if (alive) setSrc(null); });
    } else {
      setSrc(null);
    }
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path, version]);
  if (!src) {
    return (
      <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-bold text-[#1E3A8A]">
        {initials || <User className="h-4 w-4 text-slate-400" />}
      </div>
    );
  }
  return <img src={src} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-200" />;
}

function Row({ id, name, sub, photoPath, photoVersion, onUpload, onPrint, printing, uploading, testPrefix }) {
  const fileRef = useRef(null);
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`${testPrefix}-row-${id}`}>
      <PhotoCell path={photoPath} initials={initials} version={photoVersion} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-slate-800 truncate">{name}</div>
        <div className="text-xs text-slate-500 truncate">{sub}</div>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden"
             onChange={async (e) => { const f = e.target.files?.[0]; if (f) await onUpload(f); e.target.value = ""; }} />
      <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => fileRef.current?.click()}
              disabled={uploading} data-testid={`${testPrefix}-upload-${id}`}>
        {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}Photo
      </Button>
      <Button size="sm" className="bg-[#1E3A8A] hover:bg-blue-900 text-white gap-1 text-xs" onClick={onPrint}
              disabled={printing} data-testid={`${testPrefix}-print-${id}`}>
        {printing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Printer className="h-3 w-3" />}Imprimer
      </Button>
    </div>
  );
}

export default function CardsPage() {
  const [tab, setTab] = useState("students");
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [busy, setBusy] = useState(null); // id en cours (upload/print)
  const [photoVersion, setPhotoVersion] = useState(0);

  const load = async () => {
    try {
      const [s, u] = await Promise.all([api.get("/students"), api.get("/cards/staff-list")]);
      setStudents(s.data);
      setStaff(u.data);
    } catch { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { load(); }, []);

  const classes = [...new Set(students.map(s => s.class_name).filter(Boolean))].sort();
  const filteredStudents = students.filter(s => {
    const okClass = classFilter === "all" || s.class_name === classFilter;
    const okSearch = !search || `${s.first_name} ${s.last_name} ${s.matricule || ""}`.toLowerCase().includes(search.toLowerCase());
    return okClass && okSearch;
  });
  const filteredStaff = staff.filter(u => !search || `${u.full_name} ${u.email || ""}`.toLowerCase().includes(search.toLowerCase()));

  const uploadPhoto = async (kind, id, file) => {
    if (file.size > 2_000_000) return toast.error("Photo trop volumineuse (max 2 Mo)");
    setBusy(`up-${id}`);
    try {
      const b64 = await fileToBase64(file);
      await api.post(`/cards/${kind}/${id}/photo`, { photo_base64: b64 });
      toast.success("Photo importée");
      setPhotoVersion(v => v + 1);
      if (kind === "student") setStudents(prev => prev.map(s => s.id === id ? { ...s, has_photo: true } : s));
      else setStaff(prev => prev.map(u => u.id === id ? { ...u, has_photo: true } : u));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'import"); }
    finally { setBusy(null); }
  };

  const printOne = async (url, id) => {
    setBusy(`pr-${id}`);
    try { await openPdf(url); }
    catch (e) { toast.error(e.response?.status === 404 ? "Introuvable" : "Erreur d'impression"); }
    finally { setBusy(null); }
  };

  const printBatch = async () => {
    setBusy("batch");
    try {
      if (tab === "students") {
        await openPdf(`/cards/students/batch${classFilter !== "all" ? `?class_name=${encodeURIComponent(classFilter)}` : ""}`);
      } else {
        await openPdf("/cards/staff/batch");
      }
    } catch (e) { toast.error(e.response?.status === 404 ? "Aucune fiche à imprimer" : "Erreur"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="cards-page">
      <div>
        <div className="chip mb-2"><CreditCard className="h-3 w-3" /><span>IMPRESSION</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Cartes & badges</h1>
        <p className="text-slate-500 mt-1">Importez les photos puis imprimez les cartes scolaires et badges professionnels (planches A4 avec traits de coupe, format carte bancaire).</p>
      </div>

      {/* Onglets */}
      <div className="flex gap-2">
        <button onClick={() => setTab("students")}
                className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition flex items-center gap-2 ${tab === "students" ? "border-[#1E3A8A] bg-blue-50 text-[#1E3A8A]" : "border-slate-200 text-slate-500"}`}
                data-testid="tab-students">
          <CreditCard className="h-4 w-4" />Cartes scolaires ({students.length})
        </button>
        <button onClick={() => setTab("staff")}
                className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition flex items-center gap-2 ${tab === "staff" ? "border-[#EA580C] bg-orange-50 text-[#EA580C]" : "border-slate-200 text-slate-500"}`}
                data-testid="tab-staff">
          <BadgeCheck className="h-4 w-4" />Badges professionnels ({staff.length})
        </button>
      </div>

      {/* Barre outils */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un nom, matricule..." className="pl-9" data-testid="cards-search" />
        </div>
        {tab === "students" && (
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger className="w-44" data-testid="cards-class-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les classes</SelectItem>
              {classes.map(cl => <SelectItem key={cl} value={cl}>{cl}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button onClick={printBatch} disabled={busy === "batch"} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="print-batch">
          {busy === "batch" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
          {tab === "students" ? `Imprimer ${classFilter === "all" ? "toutes les cartes" : `la classe ${classFilter}`}` : "Imprimer tous les badges"}
        </Button>
      </div>

      {/* Liste */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {tab === "students" ? (
          filteredStudents.length === 0 ? (
            <div className="p-10 text-center text-slate-400">Aucun élève. Inscrivez des élèves via le module Scolarité.</div>
          ) : filteredStudents.map(s => (
            <Row key={s.id} id={s.id}
                 name={`${s.first_name} ${s.last_name}`}
                 sub={`${s.class_name || "—"} · Matricule ${s.matricule || "—"}`}
                 photoPath={s.has_photo ? `/cards/student/${s.id}/photo` : null}
                 photoVersion={photoVersion}
                 onUpload={(f) => uploadPhoto("student", s.id, f)}
                 onPrint={() => printOne(`/cards/student/${s.id}/card`, s.id)}
                 uploading={busy === `up-${s.id}`} printing={busy === `pr-${s.id}`}
                 testPrefix="student" />
          ))
        ) : (
          filteredStaff.map(u => (
            <Row key={u.id} id={u.id}
                 name={u.full_name}
                 sub={`${ROLE_LABELS[u.role]} · ${u.email || u.phone || ""}`}
                 photoPath={u.has_photo ? `/cards/staff/${u.id}/photo` : null}
                 photoVersion={photoVersion}
                 onUpload={(f) => uploadPhoto("staff", u.id, f)}
                 onPrint={() => printOne(`/cards/staff/${u.id}/badge`, u.id)}
                 uploading={busy === `up-${u.id}`} printing={busy === `pr-${u.id}`}
                 testPrefix="staff" />
          ))
        )}
      </div>

      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-4">
        💡 <strong>Conseils d&apos;impression</strong> : photos d&apos;identité au format portrait (JPEG/PNG, max 2 Mo). Les planches A4 contiennent
        8 cartes avec traits de coupe — imprimez sur papier cartonné 250 g ou plastifiez après découpe. Le logo et les couleurs
        proviennent de la Config école.
      </div>
    </div>
  );
}
